import { useState } from 'react';
import { useRouter } from 'next/router';

import { Heading, Modal } from '@zillow/constellation-10';

import { capitalize } from '@zg-rentals/zdash';
import { RentalManagerUserClaimType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserClaimWebApiHandler_add,
  useRentalManagerUserClaimWebApiHandler_propertyLookup,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import ModalFooter from '../ModalFooter';
import ChooseMethod from './components/ChooseMethod';
import ContactMethodLookup from './components/ContactMethodLookup';
import SearchResults from './components/SearchResults';
import { SKELETON_LISTING } from '../../ListingClaim/WizFindListingResults';

const STEPS = {
  INIT: 'init',
  CONTACT_METHOD_LOOKUP: 'contactMethodLookup',
  RESULTS: 'results',
} as const;

type StepKey = (typeof STEPS)[keyof typeof STEPS];

type ContactMethodItem = { value: string; isVerified?: boolean; canBeRemoved?: boolean };

export type PropertyLookupModalProps = {
  isOpen?: boolean;
  onClose: () => void;
  onContactMethodAddedSuccess: () => void;
  contactMethods?: { emails: Array<ContactMethodItem>; phones: Array<ContactMethodItem> };
};

const PropertyLookupModal = ({
  isOpen = true,
  onClose,
  onContactMethodAddedSuccess,
  contactMethods = { emails: [], phones: [] },
}: PropertyLookupModalProps) => {
  const router = useRouter();
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentStep, setCurrentStep] = useState<StepKey>(STEPS.INIT);
  const [contactValue, setContactValue] = useState('');
  const [contactMethod, setContactMethod] = useState<'phone' | 'email' | ''>('');

  const { mutateAsync: addContactMethod, isPending: isSubmitting } = useRentalManagerUserClaimWebApiHandler_add();

  const {
    data: propertyLookupData,
    refetch: refetchPropertyLookup,
    isFetching: isPropertyLookupFetching,
  } = useRentalManagerUserClaimWebApiHandler_propertyLookup(
    {
      type: contactMethod === 'email' ? RentalManagerUserClaimType.EMAIL : RentalManagerUserClaimType.PHONE,
      value: contactValue,
    },
    {
      enabled: false,
    },
  );

  const handleSearchListings = () => {
    refetchPropertyLookup();
    setCurrentStep(STEPS.RESULTS);
  };

  const handleChooseAnotherContactMethod = () => {
    setContactMethod('');
    setContactValue('');
    setHasError(false);
    setErrorMessage('');
    setCurrentStep(STEPS.INIT);
  };

  const handleSwitchContactMethods = () => {
    setContactMethod(contactMethod === 'phone' ? 'email' : 'phone');
    setContactValue('');
    setCurrentStep(STEPS.CONTACT_METHOD_LOOKUP);
  };

  const handleContactMethodLookup = (contactType: 'email' | 'phone') => {
    setContactMethod(contactType);
    setCurrentStep(STEPS.CONTACT_METHOD_LOOKUP);
  };

  const handleAddContactMethodClick = async () => {
    if (!contactMethod) return;
    setHasError(false);
    setErrorMessage('');
    try {
      await addContactMethod({
        getParams: {
          type: contactMethod === 'email' ? RentalManagerUserClaimType.EMAIL : RentalManagerUserClaimType.PHONE,
          value: contactValue,
        },
      });
      onClose();
      onContactMethodAddedSuccess();
    } catch (err) {
      setHasError(true);
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  const renderContent = () => {
    if (currentStep === STEPS.INIT) {
      return <ChooseMethod onContactMethodLookup={handleContactMethodLookup} />;
    }
    if (currentStep === STEPS.CONTACT_METHOD_LOOKUP) {
      return (
        <ContactMethodLookup
          contactMethod={contactMethod}
          contactValue={contactValue}
          onSwitchContactMethods={handleSwitchContactMethods}
          onSearchListings={handleSearchListings}
          setContactValue={setContactValue}
          contactMethods={contactMethods}
        />
      );
    }
    if (currentStep === STEPS.RESULTS) {
      const resultsListings = isPropertyLookupFetching
        ? Array(5).fill(SKELETON_LISTING)
        : (propertyLookupData?.listings ?? []);
      const resultsCount = resultsListings.length;
      return (
        <SearchResults
          contactMethod={contactMethod}
          contactValue={contactValue}
          listings={resultsListings}
          listingCount={resultsCount}
          onAddContactMethod={handleAddContactMethodClick}
          onChooseAnotherContactMethod={handleChooseAnotherContactMethod}
          errorMessage={errorMessage}
          isPropertyLookupFetching={isPropertyLookupFetching}
        />
      );
    }
    return null;
  };

  const renderFooter = () => {
    if (currentStep === STEPS.RESULTS && (propertyLookupData?.listings?.length ?? 0) > 0) {
      return (
        <ModalFooter
          hasError={hasError}
          errorMessage={errorMessage}
          isSubmitting={isSubmitting}
          cancelText="Cancel"
          submitText="Add listings"
          onCancel={onClose}
          onSubmit={handleAddContactMethodClick}
        />
      );
    }
    return null;
  };

  const MODAL_HEADING_MAPPING = {
    [STEPS.INIT]: 'Choose a property lookup method',
    [STEPS.CONTACT_METHOD_LOOKUP]: `${capitalize(contactMethod)} lookup`,
    [STEPS.RESULTS]: 'Listing search',
  };

  return (
    <Modal
      header={<Heading level={2}>{MODAL_HEADING_MAPPING[currentStep] ?? 'Property lookup'}</Heading>}
      body={renderContent()}
      footer={renderFooter()}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default PropertyLookupModal;
